import * as Sentry from '@sentry/react';
import {createBrowserRouter, RouteObject} from 'react-router';
import {HELP_URL_LOCALES} from '../help-center/locales';
import AppContainer from './AppContainer';
import RouteErrorBoundary from './RouteErrorBoundary';
import {authRoutes} from './auth/routes';
import {dashboardRoutes} from './dashboard/routes';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// The help content routes exist once per locale: at `/help` (English) and
// under each `/help/<locale>` prefix. Fresh objects per call — route objects
// must not be shared between branches of the tree.
const helpContentRoutes = (): RouteObject[] => [
  {
    path: '',
    lazy: () => import('../screens/HelpCenter/ArticleListScreen'),
  },
  {
    path: 'category/:category',
    lazy: () => import('../screens/HelpCenter/CategoryScreen'),
  },
  {
    path: ':slug',
    lazy: () => import('../screens/HelpCenter/ArticleViewScreen'),
  },
];

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <AppContainer />,
    // Catches render/loader errors from any descendant route and forwards them
    // to Sentry (the default React Router boundary swallows them silently).
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '',
        lazy: () => import('../screens/LandingPage'),
      },
      {
        path: 'auth',
        children: authRoutes,
      },
      {
        path: 'map',
        lazy: () => import('../screens/WeatherMap'),
      },
      {
        // Top-level (like /map) so it renders full-width without the dashboard
        // nav rail. Flag-gated (forecast_maps): the screen redirects when off.
        path: 'forecast-maps',
        lazy: () => import('../screens/ForecastMaps'),
      },
      {
        path: 'forecast-maps/builder',
        lazy: () => import('../screens/ForecastMaps/Builder'),
      },
      {
        // Stub for now (filled by #46 Part 2). Flag-gated (forecast_maps):
        // the screen redirects when off.
        path: 'forecast-maps/zones',
        lazy: () => import('../screens/ForecastMaps/Zones'),
      },
      {
        path: 'forecast-maps/models',
        lazy: () => import('../screens/ForecastMaps/Models'),
      },
      {
        path: 'app',
        lazy: () => import('./dashboard'),
        children: dashboardRoutes,
      },
      {
        path: 'terms-and-conditions',
        lazy: () => import('../screens/legal/TermsOfService'),
      },
      {
        path: 'privacy-policy',
        lazy: () => import('../screens/legal/PrivacyPolicy'),
      },
      {
        path: 'account-deletion',
        lazy: () => import('../screens/legal/AccountDeletionInformation'),
      },
      {
        path: 'help',
        lazy: () => import('../screens/HelpCenter'),
        children: [
          // Locale-prefixed help URLs (#70): static locale segments declared
          // before `:slug` so `/help/fr` can never be read as an article (no
          // article slug collides with a locale code). English stays at the
          // bare, historically-indexed URLs.
          ...HELP_URL_LOCALES.map((locale) => ({
            path: locale,
            children: helpContentRoutes(),
          })),
          ...helpContentRoutes(),
        ],
      },
      {
        path: '*',
        lazy: async () => import('../screens/NotFound'),
      },
    ],
  },
]);

export default router;
